import { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Users, X } from "lucide-react";
import { useContactsPaginated, type ChatFilters } from "@/hooks/useContactsPaginated";
import { useTags } from "@/hooks/useTags";
import {
  getDisplayName,
  getContactAvatarUrl,
  formatLastMessageDate,
  isContactUnread,
  formatWhatsAppJid,
} from "@/utils/contactUtils";
import type { Database } from "@/lib/supabase";
import { MobileTagChip } from "../components/MobileTagChip";

type AccountContact = Database["public"]["Tables"]["account_contacts"]["Row"];

const defaultFilters: ChatFilters = {
  attributionId: null,
  statusChat: null,
  tag: null,
};

const MAX_VISIBLE_TAGS = 3;

const ContactRow = ({
  contact,
  tagColors,
  onSelect,
}: {
  contact: AccountContact;
  tagColors: Record<string, string>;
  onSelect: (contact: AccountContact) => void;
}) => {
  const unread = isContactUnread(contact);
  const name = getDisplayName(contact);
  const tags = contact.tags ?? [];
  const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
  const hiddenTagCount = tags.length - visibleTags.length;

  return (
    <button
      type="button"
      onClick={() => onSelect(contact)}
      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors active:bg-hover"
    >
      <div className="relative shrink-0">
        <img
          src={getContactAvatarUrl(contact)}
          alt=""
          className="h-12 w-12 rounded-full object-cover"
          loading="lazy"
        />
        {contact.is_group && (
          <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full border-2 border-background bg-card-muted">
            <Users className="h-2.5 w-2.5 text-muted-foreground" />
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1 border-b border-border-soft pb-3">
        <div className="flex items-baseline justify-between gap-2">
          <p
            className={`truncate text-[15px] ${
              unread ? "font-semibold text-foreground" : "font-medium text-foreground"
            }`}
          >
            {name}
          </p>
          <span
            className={`shrink-0 text-[11px] ${
              unread ? "font-semibold text-accent-blue" : "text-muted-foreground"
            }`}
          >
            {contact.last_message_at ? formatLastMessageDate(contact.last_message_at) : ""}
          </span>
        </div>
        <div className="mt-0.5 flex items-center justify-between gap-2">
          <p className="truncate text-[13px] text-muted-foreground">
            {contact.phone || formatWhatsAppJid(contact.jid)}
          </p>
          {unread && <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-accent-blue" />}
        </div>
        {visibleTags.length > 0 && (
          <div className="mt-1.5 flex items-center gap-1 overflow-hidden">
            {visibleTags.map((tag) => (
              <MobileTagChip key={tag} name={tag} color={tagColors[tag]} />
            ))}
            {hiddenTagCount > 0 && (
              <span className="shrink-0 text-[11px] font-medium text-muted-foreground">
                +{hiddenTagCount}
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );
};

const SkeletonRow = () => (
  <div className="flex animate-pulse items-center gap-3 px-4 py-3">
    <div className="h-12 w-12 shrink-0 rounded-full bg-card-muted" />
    <div className="flex-1 space-y-2">
      <div className="h-3.5 w-2/5 rounded bg-card-muted" />
      <div className="h-3 w-3/5 rounded bg-card-muted" />
    </div>
  </div>
);

const MobileChatList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { contacts, loading, hasMore, loadMore } = useContactsPaginated(defaultFilters);
  const { tags } = useTags();
  const sentinelRef = useRef<HTMLDivElement>(null);

  const tagColors = useMemo(
    () => Object.fromEntries(tags.map((tag) => [tag.name, tag.color])),
    [tags]
  );

  const filteredContacts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return contacts;
    return contacts.filter((contact) => {
      const name = getDisplayName(contact).toLowerCase();
      const phone = (contact.phone || contact.jid || "").toLowerCase();
      return name.includes(term) || phone.includes(term);
    });
  }, [contacts, search]);

  // Paginação por scroll: carrega mais quando a sentinela entra na viewport
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadMore]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="shrink-0 border-b border-border bg-card px-4 pb-3 pt-3">
        <h1 className="text-xl font-bold text-foreground">Conversas</h1>
        <div className="relative mt-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            inputMode="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou telefone"
            className="h-10 w-full rounded-full border border-border bg-background pl-9 pr-9 text-[14px] text-foreground outline-none placeholder:text-muted-foreground focus:border-accent-blue"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              aria-label="Limpar busca"
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground active:bg-hover"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {loading && contacts.length === 0 ? (
          <div>
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-8 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-card-muted">
              <Search className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-[15px] font-semibold text-foreground">
              {search ? "Nenhum resultado" : "Nenhuma conversa"}
            </p>
            <p className="text-[13px] text-muted-foreground">
              {search
                ? "Tente buscar por outro nome ou telefone."
                : "As conversas do WhatsApp aparecerão aqui."}
            </p>
          </div>
        ) : (
          <>
            {filteredContacts.map((contact) => (
              <ContactRow
                key={contact.id}
                contact={contact}
                tagColors={tagColors}
                onSelect={(c) => navigate(`/m/chat/${c.id}`)}
              />
            ))}
            {/* Sentinela de paginação: só quando não há busca ativa */}
            {!search && hasMore && (
              <div ref={sentinelRef} className="flex items-center justify-center py-4">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MobileChatList;
